/**
 * OmegaCeoGiTile — CEO GI summary tile for the L1 OMEGA dashboard.
 *
 * Fetches the latest assembled CEO GI payload from
 * `GET /api/v1/admin/omega/ceo-gi/latest` and displays:
 *
 *   - synthesis_summary   — main narrative text block
 *   - gap_summary         — secondary text block
 *   - convergence_score   — shown as a percentage
 *   - layers_covered      — count
 *   - assembled_at        — relative time
 *
 * Polls every 5 minutes (matches the brain runtime's assembly cadence).
 * Returns gracefully when no payload exists yet (404 → "Not assembled yet").
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch, ApiHttpError } from '@/api/http';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CeoGIPayload {
  id: string;
  assembled_at_unix_nanos: number;
  synthesis_summary: string;
  layers_covered: number;
  convergence_score: number;
  gap_summary: string;
}

// ── Fetch ─────────────────────────────────────────────────────────────────────

async function fetchLatestCeoGi(): Promise<CeoGIPayload | null> {
  return apiFetch<CeoGIPayload>('/admin/omega/ceo-gi/latest').catch((err: unknown) => {
    if (err instanceof ApiHttpError && err.notFound) return null;
    throw err;
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function assembledAgo(nanos: number): string {
  const ms = nanos / 1_000_000;
  const diffMs = Date.now() - ms;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'less than a minute ago';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OmegaCeoGiTile() {
  const { data, isPending, isError } = useQuery<CeoGIPayload | null>({
    queryKey: ['omega', 'ceo-gi', 'latest'],
    queryFn: fetchLatestCeoGi,
    refetchInterval: 5 * 60_000, // 5 minutes
    staleTime: 4 * 60_000,
    retry: (count, err) => {
      // Don't retry on auth failures
      if (err instanceof Error && err.message.includes('HTTP 401')) return false;
      return count < 2;
    },
  });

  const convergencePct =
    data != null ? Math.round(data.convergence_score * 100) : null;

  return (
    <section
      aria-labelledby="ceo-gi-heading"
      style={{
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-3)',
        marginTop: 'var(--omega-gap-3)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--omega-gap-2)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <h2
          id="ceo-gi-heading"
          style={{
            margin: 0,
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          CEO GI
        </h2>

        {convergencePct != null && (
          <span
            style={{
              fontSize: '9pt',
              padding: '1px 7px',
              border: '1px solid var(--omega-rim-strong)',
              color:
                convergencePct >= 70
                  ? 'var(--omega-state-healthy)'
                  : convergencePct >= 40
                    ? 'var(--omega-state-degraded)'
                    : 'var(--omega-state-refused)',
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          >
            {convergencePct}% convergence
          </span>
        )}

        {data != null && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: '8.5pt',
              color: 'var(--omega-ink-subtle)',
              letterSpacing: '0.04em',
            }}
          >
            {assembledAgo(data.assembled_at_unix_nanos)}
          </span>
        )}
      </div>

      {/* Body */}
      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '10pt' }}>
          Loading…
        </div>
      )}

      {isError && (
        <div style={{ color: 'var(--omega-state-refused)', fontSize: '10pt' }}>
          Failed to load CEO GI payload.
        </div>
      )}

      {!isPending && !isError && data === null && (
        <div
          style={{
            color: 'var(--omega-ink-subtle)',
            fontSize: '10pt',
            fontStyle: 'italic',
          }}
        >
          No CEO GI assembled yet — will generate on next brain cycle (every 5
          minutes).
        </div>
      )}

      {!isPending && !isError && data != null && (
        <>
          {/* layers covered */}
          <div
            style={{
              fontSize: '8.5pt',
              color: 'var(--omega-ink-subtle)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: 'var(--omega-gap-2)',
            }}
          >
            {data.layers_covered} knowledge layers covered
          </div>

          {/* synthesis_summary */}
          <div
            style={{
              fontSize: '10pt',
              color: 'var(--omega-ink)',
              marginBottom: 'var(--omega-gap-2)',
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
            }}
          >
            {data.synthesis_summary}
          </div>

          {/* gap_summary */}
          {data.gap_summary && (
            <>
              <div
                style={{
                  fontSize: '8.5pt',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--omega-ink-subtle)',
                  marginBottom: 6,
                  borderTop: '1px solid var(--omega-rim)',
                  paddingTop: 'var(--omega-gap-1)',
                }}
              >
                Gap Summary
              </div>
              <div
                style={{
                  fontSize: '10pt',
                  color: 'var(--omega-ink-muted)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {data.gap_summary}
              </div>
            </>
          )}

          {/* Link to CEO dashboard */}
          <div
            style={{
              marginTop: 'var(--omega-gap-2)',
              paddingTop: 'var(--omega-gap-1)',
              borderTop: '1px solid var(--omega-rim)',
            }}
          >
            <Link
              to="/omega/ceo"
              style={{
                fontSize: '8.5pt',
                color: 'var(--omega-ink-subtle)',
                letterSpacing: '0.06em',
                textDecoration: 'none',
              }}
            >
              Open CEO dashboard →
            </Link>
          </div>
        </>
      )}
    </section>
  );
}
